import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Admin / approver "Cancellation Requests" inbox.
 *
 * Each row carries everything needed to make a decision without a second
 * lookup (doc summary, requester, reason). Decisions are recorded against
 * the request AND mutate the source document atomically on the backend.
 */
const KIND_LABEL = {
  sales_invoice:  'Sales Invoice',
  purchase_order: 'Purchase Order',
  delivery_note:  'Delivery Note',
  payment:        'Payment',
};

const STATUS_TONE = {
  pending:  'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-800',
};

const CancellationRequests = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const r = await api.get('/cancellation-requests', { params });
      setRows(r.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const approve = async (id) => {
    if (!window.confirm('Approve this cancellation? The document will be permanently cancelled and stock movements reversed.')) return;
    try {
      await api.post(`/cancellation-requests/${id}/approve`);
      fetchData();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Approve failed');
    }
  };

  const reject = async (id) => {
    const reason = window.prompt('Rejection reason (shown to requester):');
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post(`/cancellation-requests/${id}/reject`, { reason: reason.trim() });
      fetchData();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Reject failed');
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="cancellation-requests-page">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">🗑 Cancellation Requests</h1>
          <p className="text-sm text-slate-600 mt-1">Approve or reject document cancellation requests from data-entry users.</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            className="border rounded px-2 py-1.5 text-sm bg-white"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="cancellation-status-filter"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="all">All</option>
          </select>
          <button
            onClick={fetchData}
            className="bg-indigo-600 text-white text-sm px-3 py-1.5 rounded hover:bg-indigo-700"
          >Refresh</button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded text-sm text-rose-800">{error}</div>
      )}

      <div className="border rounded-md overflow-hidden bg-white">
        <table className="w-full text-sm" data-testid="cancellation-requests-table">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="px-3 py-2 text-left">Requested</th>
              <th className="px-3 py-2 text-left">Doc</th>
              <th className="px-3 py-2 text-left">Party</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-left">Requester</th>
              <th className="px-3 py-2 text-left">Reason</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">No requests.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t align-top hover:bg-slate-50">
                <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                  {new Date(r.requested_at).toLocaleString()}
                </td>
                <td className="px-3 py-2">
                  <div className="font-semibold text-slate-900">{KIND_LABEL[r.doc_kind] || r.doc_kind}</div>
                  <div className="text-xs font-mono text-slate-600">{r.doc_summary?.ref || r.doc_id}</div>
                  {r.doc_summary?.date && (
                    <div className="text-xs text-slate-500">{r.doc_summary.date}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-slate-800">{r.doc_summary?.party || '—'}</td>
                <td className="px-3 py-2 text-right text-slate-900 font-medium">
                  {r.doc_summary?.amount != null ? Number(r.doc_summary.amount).toFixed(3) : '—'}
                </td>
                <td className="px-3 py-2 text-slate-700">{r.requested_by_name}</td>
                <td className="px-3 py-2 text-slate-700 max-w-xs">
                  <div className="line-clamp-3">{r.reason}</div>
                  {r.note && <div className="text-xs italic text-slate-500 mt-1">{r.note}</div>}
                  {r.status === 'rejected' && r.rejection_reason && (
                    <div className="text-xs text-rose-700 mt-1"><b>Rejected:</b> {r.rejection_reason}</div>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${STATUS_TONE[r.status] || 'bg-slate-100 text-slate-800'}`}>
                    {r.status}
                  </span>
                  {r.decided_by_name && (
                    <div className="text-xs text-slate-500 mt-1">by {r.decided_by_name}</div>
                  )}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {r.status === 'pending' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => approve(r.id)}
                        className="px-2.5 py-1 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700"
                        data-testid={`cancel-approve-${r.id}`}
                      >Approve</button>
                      <button
                        onClick={() => reject(r.id)}
                        className="px-2.5 py-1 text-xs rounded border border-rose-300 text-rose-700 hover:bg-rose-50"
                        data-testid={`cancel-reject-${r.id}`}
                      >Reject</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CancellationRequests;
